/** @jsxImportSource @emotion/react */
import styled from '@emotion/styled'
import React from 'react'
import Text from '../../Text'
import colors from '@sportninja/common/constants/appColors'
import { PRIMARY_BUTTON_CONTENT } from '@sportninja/common/constants/buttonColors'
import CheckmarkIcon from '../../icons/CheckmarkIcon'

interface Step {
  number: number
  title: string
  subtitle: string
  isActive: boolean
  isCompleted: boolean
}

interface ProgressHeaderProps {
  currentStep: number
}

const HeaderContainer = styled.div<{ currentStep: number }>`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 16px;
  background: ${colors.SECONDARY_500};
  gap: 8px;
  margin-bottom: 16px;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 32px;
    left: 32px;
    right: ${(props) => {
      // Don't show the base line beyond the last step
      if (props.currentStep === 3) {
        return 'calc(100% - 32px)'
      }
      return '32px'
    }};
    height: 1px;
    background: ${colors.SECONDARY_200};
    z-index: 1;
  }

  &::after {
    content: '';
    position: absolute;
    top: 32px;
    left: 32px;
    width: ${(props) => {
      // Calculate the width of the completed line based on current step
      if (props.currentStep === 1) {
        return '0px'
      }
      if (props.currentStep === 2) {
        return 'calc(50% - 16px)'
      }
      if (props.currentStep === 3) {
        return 'calc(100% - 80px)'
      }
      return '0px'
    }};
    height: 1px;
    background: ${colors.PRIMARY};
    z-index: 1;
    transition: width 0.3s ease;
  }

  @media (max-width: 1024px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
    padding: 16px;
    overflow-x: auto;
    max-width: 100vw;

    &::before,
    &::after {
      display: none;
    }
  }
`

const StepItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  position: relative;
  z-index: 2;
  background: ${colors.SECONDARY_500};
  padding: 0 8px;

  @media (max-width: 1024px) {
    min-width: 0;
    padding: 0 4px;
  }
`

const StepNumber = styled.div<{ isActive: boolean; isCompleted: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(props) =>
    props.isActive ? colors.PRIMARY : colors.SECONDARY_400};
  color: ${(props) =>
    props.isActive ? PRIMARY_BUTTON_CONTENT.text : colors.NEUTRAL_100};
  flex-shrink: 0;
  position: relative;
  z-index: 2;
  opacity: ${(props) => (props.isActive || props.isCompleted ? 1 : 0.5)};
`

const StepContent = styled.div<{ isActive: boolean; isCompleted: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 2px;
  opacity: ${(props) => (props.isActive || props.isCompleted ? 1 : 0.5)};
`

/**
 * Progress header component for the competition setup wizard
 * Shows the current step and progress through the wizard
 */
const ProgressHeader: React.FC<ProgressHeaderProps> = ({ currentStep }) => {
  const steps: Step[] = [
    {
      number: 1,
      title: 'Competition Structure',
      subtitle: 'Choose Competition Structure',
      isActive: currentStep === 1,
      isCompleted: currentStep > 1,
    },
    {
      number: 2,
      title: 'Divisions',
      subtitle: 'Create Divisions',
      isActive: currentStep === 2,
      isCompleted: currentStep > 2,
    },
    {
      number: 3,
      title: 'Registration',
      subtitle: 'Configure Sign-ups',
      isActive: currentStep === 3,
      isCompleted: false,
    },
  ]

  return (
    <HeaderContainer currentStep={currentStep}>
      {steps.map((step) => (
        <React.Fragment key={step.number}>
          <StepItem>
            <StepNumber isActive={step.isActive} isCompleted={step.isCompleted}>
              {step.isCompleted ? (
                <CheckmarkIcon size={16} color={colors.PRIMARY} />
              ) : (
                <Text
                  variant='paragraphSmall'
                  weight='regular'
                  color={
                    step.isActive
                      ? PRIMARY_BUTTON_CONTENT.text
                      : colors.NEUTRAL_100
                  }
                >
                  {step.number}
                </Text>
              )}
            </StepNumber>
            <StepContent
              isActive={step.isActive}
              isCompleted={step.isCompleted}
            >
              <Text
                variant='paragraphMedium'
                weight='medium'
                color={
                  step.isActive || step.isCompleted
                    ? colors.NEUTRAL_0
                    : colors.NEUTRAL_100
                }
              >
                {step.title}
              </Text>
              <Text
                variant='paragraphXSmall'
                weight='regular'
                color={colors.NEUTRAL_30}
              >
                {step.subtitle}
              </Text>
            </StepContent>
          </StepItem>
        </React.Fragment>
      ))}
    </HeaderContainer>
  )
}

export default ProgressHeader
